'use client';

import { useEffect, useState } from 'react';

const VWO_READY_TIMEOUT = 3000;
const VWO_POLL_INTERVAL = 50;

function getQueryParamVariation(expId: string): string | null {
	if (typeof window === 'undefined') return null;

	const urlParams = new URLSearchParams(window.location.search);

	const testId = urlParams.get('_vis_test_id');
	const previewCombination = urlParams.get('_vis_opt_preview_combination');
	if (testId === expId && previewCombination) return previewCombination;

	const vwoTestParam = urlParams.get(`vwo_test_${expId}`);
	if (vwoTestParam) return vwoTestParam;

	const combiParam = urlParams.get(`_vis_opt_exp_${expId}_combi`);
	if (combiParam) return combiParam;

	const splitParam = urlParams.get(`_vis_opt_exp_${expId}_split`);
	if (splitParam) return splitParam;

	// VWO preview links use a base64-encoded JSON blob in _vis_preview_data
	// containing experiment-to-variation mappings: { e: { [expId]: { v: "2" } } }
	const previewData = urlParams.get('_vis_preview_data');
	if (previewData) {
		try {
			const parsed = JSON.parse(atob(previewData)) as {
				e?: Record<string, { v?: string }>;
			};
			const expData = parsed?.e?.[expId];
			if (expData?.v !== undefined) return String(expData.v);
		} catch {
			// invalid base64 or JSON, skip
		}
	}

	return null;
}

function getVariationFromCookies(expId: string): string | null {
	const cookies = document.cookie;

	const previewPattern = /debug_vis_preview_\d+=([^;]+)/g;
	let previewMatch: RegExpExecArray | null;
	while ((previewMatch = previewPattern.exec(cookies)) !== null) {
		try {
			const parsed = JSON.parse(decodeURIComponent(previewMatch[1])) as Record<string, { v?: string }>;
			if (parsed[expId]?.v !== undefined) return parsed[expId].v ?? null;
		} catch {
			// invalid JSON, skip
		}
	}

	/* eslint-disable security/detect-non-literal-regexp */
	const patterns = [
		new RegExp(`debug_vis_opt_exp_${expId}_(combi|split)=([^;]+)`),
		new RegExp(`_vis_opt_exp_${expId}_(combi|split)=([^;]+)`),
		new RegExp(`vis_opt_exp_${expId}_(combi|split)=([^;]+)`),
	];
	/* eslint-enable security/detect-non-literal-regexp */

	for (const pattern of patterns) {
		const cookieMatch = pattern.exec(cookies);
		if (cookieMatch?.[2]) return cookieMatch[2];
	}

	return null;
}

function getVariationFromVwoExp(expId: string): string | null {
	if (!globalThis._vwo_exp || typeof globalThis._vwo_exp !== 'object') return null;

	const expVariation = globalThis._vwo_exp[expId];
	if (expVariation === undefined) return null;

	if (typeof expVariation === 'object' && expVariation !== null) {
		const obj = expVariation as unknown as Record<string, unknown>;
		const raw = obj.combination_chosen ?? obj.combination ?? obj.c ?? obj.variation ?? obj.variationId ?? obj.v;
		if (raw !== undefined && (typeof raw === 'string' || typeof raw === 'number')) {
			const varId = String(raw);
			if (varId !== expId) return varId;
		}
	} else if (String(expVariation) !== expId) {
		return expVariation.toString();
	}

	return null;
}

function getVariationFromCampaignData(expId: string): string | null {
	if (!globalThis._vwo_campaignData || typeof globalThis._vwo_campaignData !== 'object') return null;

	const campaignData = globalThis._vwo_campaignData[expId];
	if (campaignData?.c !== undefined) return campaignData.c.toString();

	return null;
}

function getVariationFromVwoSdk(expId: string): string | null {
	const campaign = window.VWO?._?.allSettings?.campaigns?.[expId];
	if (campaign?.variation) return campaign.variation.toString();

	return null;
}

export function getExperimentVariation(experimentId: string): string | null {
	if (typeof window === 'undefined') return null;

	const expId = experimentId.toString();

	const queryParamVariation = getQueryParamVariation(expId);
	if (queryParamVariation !== null) return queryParamVariation;

	const cookieVariation = getVariationFromCookies(expId);
	if (cookieVariation !== null) return cookieVariation;

	const vwoExpVariation = getVariationFromVwoExp(expId);
	if (vwoExpVariation !== null) return vwoExpVariation;

	const campaignVariation = getVariationFromCampaignData(expId);
	if (campaignVariation !== null) return campaignVariation;

	const sdkVariation = getVariationFromVwoSdk(expId);
	if (sdkVariation !== null) return sdkVariation;

	return null;
}

export interface UseVWOExperimentReturn {
	variationId: string | null;
	isReady: boolean;
}

export function useVWOExperiment(experimentId: string | null | undefined): UseVWOExperimentReturn {
	const [isReady, setIsReady] = useState(false);
	const [variationId, setVariationId] = useState<string | null>(null);

	useEffect(() => {
		if (!experimentId) {
			setIsReady(true);
			return;
		}

		// Check if variation data is already available (query params, cookies, globals).
		// This resolves immediately for VWO preview links with _vis_preview_data in the URL,
		// without waiting for the VWO script to finish loading.
		const immediateVariation = getExperimentVariation(experimentId);
		if (immediateVariation !== null) {
			setVariationId(immediateVariation);
			setIsReady(true);
			return;
		}

		let isComplete = false;
		let timeoutId: ReturnType<typeof setTimeout> | null = null;

		const resolve = () => {
			if (isComplete) return;
			isComplete = true;
			setVariationId(getExperimentVariation(experimentId));
			setIsReady(true);
			if (timeoutId) clearTimeout(timeoutId);
		};

		const checkVWOReady = (): boolean => {
			if (!window._vwo_code?.finished()) return false;
			// _vwo_code.finished() is true once VWO ran on any URL in this tab, not
			// specifically for this experiment. Only short-circuit if VWO actually wrote
			// variation data for this experiment — otherwise SPA navigation to the
			// experiment URL would immediately resolve to null (control) because VWO ran
			// for a different URL and won't re-run for this one.
			if (getExperimentVariation(experimentId) !== null) {
				resolve();
				return true;
			}
			return false;
		};

		if (checkVWOReady()) return;

		// Listen for VWO ready events
		let registeredListener: 'phoenix' | 'nls' | 'queue' | null = null;

		if (window.VWO?.phoenix?.on) {
			window.VWO.phoenix.on('vwo.allSettingsApplied', resolve);
			registeredListener = 'phoenix';
		} else if (typeof window.VWO?.nls === 'function') {
			window.VWO.nls(resolve);
			registeredListener = 'nls';
		} else {
			window._vis_opt_queue = window._vis_opt_queue || [];
			window._vis_opt_queue.push(resolve);
			registeredListener = 'queue';
		}

		const pollInterval = setInterval(() => {
			if (checkVWOReady()) clearInterval(pollInterval);
		}, VWO_POLL_INTERVAL);

		timeoutId = setTimeout(() => {
			if (!isComplete) {
				isComplete = true;
				clearInterval(pollInterval);
				// On timeout, still try to read whatever variation is available
				setVariationId(getExperimentVariation(experimentId));
				setIsReady(true);
			}
		}, VWO_READY_TIMEOUT);

		return () => {
			isComplete = true;
			if (timeoutId) clearTimeout(timeoutId);
			clearInterval(pollInterval);

			// Clean up VWO event listeners
			if (registeredListener === 'queue' && window._vis_opt_queue) {
				const idx = window._vis_opt_queue.indexOf(resolve);
				if (idx !== -1) window._vis_opt_queue.splice(idx, 1);
			}
			// Note: VWO phoenix.on and nls do not provide unsubscribe APIs,
			// but the isComplete flag prevents resolve from updating state after unmount.
		};
	}, [experimentId]);

	return { variationId, isReady };
}
